import { Button } from '@blueprint/ui';
import { useHistory, useParams } from "react-router-dom";

import { UserStatus, User } from "../../../types/users";
import { UserRole, UserCurrent } from "../../../types/userCurrent";
import { isUserAdmin, isUserSuperAdmin } from "../../../utils/role";
import useActivateHandler from "../hooks/useActivateHandler";
import useDeactivateHandler from "../hooks/useDeactivateHandler";
import useReviewApproveHandler from "../hooks/useReviewApproveHandler";
import useReviewRejectHandler from "../hooks/useReviewRejectHandler";


type AllowedFunctionProps = {
    currentRoles: UserRole[];
    currentUser?: UserCurrent | null;
    userData: User;
};

const AllowedFunction = ({ currentRoles, currentUser, userData }: AllowedFunctionProps): JSX.Element => {
    const history = useHistory();
    const { userId } = useParams<{ userId: string }>();
    const { handler: activateHandler } = useActivateHandler(userId);
    const { handler: deactivateHandler } = useDeactivateHandler(userId);
    const { handler: reviewApproveHandler } = useReviewApproveHandler(userId);
    const { handler: reviewRejectHandler } = useReviewRejectHandler(userId);

    const editUserEle = <Button
        variant="light.primary"
        onClick={() =>
            history.push(`/user-management/user-information/${userId}/update-user`)
        }
    >
        Edit User
    </Button>;
    const activeUserEle = <Button
        className="btn-second-ml"
        variant="light.primary"
        onClick={() =>
            activateHandler(
                `${userData.firstName} ${userData.lastName}`,
            )
        }
    >
        Activate User
    </Button>
    const deactivateUserEle = <Button
        className="btn-second-ml"
        variant="light.primary"
        onClick={() =>
            deactivateHandler(
                `${userData.firstName} ${userData.lastName}`,
            )
        }
    >
        Deactivate User
    </Button>
    const approveEle = <Button
        variant="light.primary"
        onClick={() =>
            reviewApproveHandler(
                `${userData.firstName} ${userData.lastName}`,
                userData.status,
            )
        }
    >
        Approve
    </Button>;
    const rejectEle = <Button
        variant="light.tertiary"
        onClick={() =>
            reviewRejectHandler(
                `${userData.firstName} ${userData.lastName}`,
                userData.status,
            )
        }
    >
        Reject
    </Button>;

    const content = () => {
        const reg = /(?<=\()(.+?)(?=\))/g;
        const submitter = userData.submitBy?.match(reg);
        // who can see these functions? --(SuperAdmin) and (Admin if in same profile)
        if (!(isUserSuperAdmin(currentRoles) || (isUserAdmin(currentRoles) && userData.profileName === currentUser?.userProfile.name))) {
            return <></>;
        }
        switch (userData.status) {
            case UserStatus.DRAFT:
                return editUserEle;
            case UserStatus.PENDING_CREATE_APPROVAL:
            case UserStatus.PENDING_EDIT_APPROVAL:
                // cannot edit, other SuperAdmin can review it
                if (!!submitter && isUserSuperAdmin(currentRoles) && submitter[0] !== currentUser?.email) {
                    return <>{approveEle} {rejectEle}</>;
                }
                return <></>;
            case UserStatus.PENDING_ACTIVATION:
            case UserStatus.PENDING:
                return deactivateUserEle;
            case UserStatus.ACTIVE:
                return <>{editUserEle} {deactivateUserEle}</>;
            case UserStatus.INACTIVE:
                return activeUserEle;

            default:
                return <></>;
        }
    };

    return content();
};



export default AllowedFunction;
